import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select"
import type { CodexDiagnostics, CodexReasoningEffort } from "@/platform/types"

const codexDefaultModelValue = "__default__"
const fallbackReasoningEfforts: CodexReasoningEffort[] = [
	"minimal",
	"low",
	"medium",
	"high",
	"xhigh",
]

const displayReasoningEffort = (effort: CodexReasoningEffort) =>
	effort === "xhigh"
		? "Extra high"
		: `${effort.charAt(0).toUpperCase()}${effort.slice(1)}`

const selectedCodexModel = (
	diagnostics: CodexDiagnostics | null,
	model: string | null,
) =>
	diagnostics?.models.find((candidate) => candidate.model === model) ??
	diagnostics?.models.find((candidate) => candidate.isDefault)

export const reasoningEffortForModel = (
	diagnostics: CodexDiagnostics | null,
	model: string | null,
	currentEffort: CodexReasoningEffort,
) => {
	const selectedModel = selectedCodexModel(diagnostics, model)
	const supportedEfforts = selectedModel?.supportedReasoningEfforts.map(
		(option) => option.reasoningEffort,
	)
	if (!supportedEfforts?.length || supportedEfforts.includes(currentEffort)) {
		return currentEffort
	}
	return selectedModel?.defaultReasoningEffort ?? "medium"
}

export const CodexModelSelect = ({
	className,
	diagnostics,
	disabled,
	onValueChange,
	value,
}: {
	className?: string
	diagnostics: CodexDiagnostics | null
	disabled?: boolean
	onValueChange: (model: string | null) => void
	value: string | null
}) => {
	const defaultModel = diagnostics?.models.find((model) => model.isDefault)

	return (
		<Select
			value={value ?? codexDefaultModelValue}
			onValueChange={(model) =>
				onValueChange(model === codexDefaultModelValue ? null : model)
			}
			disabled={disabled || diagnostics?.status !== "ready"}
		>
			<SelectTrigger className={className} aria-label="Codex model">
				<SelectValue placeholder="Codex default" />
			</SelectTrigger>
			<SelectContent>
				<SelectItem value={codexDefaultModelValue}>
					Codex default
					{defaultModel ? ` (${defaultModel.displayName})` : ""}
				</SelectItem>
				{diagnostics?.models.map((model) => (
					<SelectItem key={model.id} value={model.model}>
						{model.displayName}
					</SelectItem>
				))}
			</SelectContent>
		</Select>
	)
}

export const CodexReasoningEffortSelect = ({
	className,
	diagnostics,
	disabled,
	model,
	onValueChange,
	value,
}: {
	className?: string
	diagnostics: CodexDiagnostics | null
	disabled?: boolean
	model: string | null
	onValueChange: (effort: CodexReasoningEffort) => void
	value: CodexReasoningEffort
}) => {
	const supportedEfforts = selectedCodexModel(
		diagnostics,
		model,
	)?.supportedReasoningEfforts
	const options = supportedEfforts?.length
		? supportedEfforts
		: fallbackReasoningEfforts.map((reasoningEffort) => ({
				description: "",
				reasoningEffort,
			}))

	return (
		<Select
			value={value}
			onValueChange={(effort) => onValueChange(effort as CodexReasoningEffort)}
			disabled={disabled || diagnostics?.status !== "ready"}
		>
			<SelectTrigger className={className} aria-label="Codex reasoning effort">
				<SelectValue />
			</SelectTrigger>
			<SelectContent>
				{options.map((option) => (
					<SelectItem
						key={option.reasoningEffort}
						value={option.reasoningEffort}
					>
						{displayReasoningEffort(option.reasoningEffort)}
					</SelectItem>
				))}
			</SelectContent>
		</Select>
	)
}
